import { COLOR } from 'app-v2/design-tokens'
import { CreditCard } from 'app/assets/icons'
import { MealVoucherPayIcon, CardCBIcon } from 'app-v2/assets/icons'
import { media } from 'app/assets/styles'
import { Collapsible } from 'app/common/collapsible'
import { Check, Radio } from 'app/common/forms'
import { CaptionText, H4, Text } from 'app/common/typography'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, IntlShape, injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { type RootState } from 'shared/store/reducers'
import { type CreditCardInfo } from 'shared/types'
import { creditCardType } from 'shared/utils/ecommerce'
import styled from 'styled-components'
import { FeatureToggle } from 'app/common/feature-toggle'
import { featureCodes } from 'shared/utils'

const creditCards = [
    {
        id: creditCardType.CB,
        labelKey: 'clickandcollect.cards.cb',
        Icon: CardCBIcon,
        lunchVoucher: false
    },
    {
        id: creditCardType.MEAL_VOUCHER,
        labelKey: 'clickandcollect.cards.mealVoucher',
        Icon: MealVoucherPayIcon,
        lunchVoucher: true
    }
]

type Props = {
    cards: Array<CreditCardInfo>,
    mealVoucherService: boolean,
    formik: any,
    intl: IntlShape,
    authenticated?: boolean,
    lightMargin?: boolean
}

const CheckBox = ({ formik, lightMargin }: Props) => {
    const { values, setFieldValue } = formik
    const { saveCard } = values

    const onClick = () => {
        setFieldValue('saveCard', !saveCard)
    }

    return (
        <CheckContainer lightMargin={lightMargin}>
            <Check
                labelKey="clickandcollect.payment.cards.save"
                labelColor={COLOR.PRIMARY}
                color={COLOR.PRIMARY}
                checked={saveCard}
                onClick={onClick}
            />
        </CheckContainer>
    )
}

const ExistingCards = ({ cards, formik, intl }: Props) => {
    const { values, setFieldValue } = formik
    const { cardIdSelected } = values
    const { authenticated } = useSelector((state: RootState) => state.userState)

    if (!authenticated || !(cards || []).length) {
        return null
    }
    return (
        <FeatureToggle
            features={[
                featureCodes.NEPTING_ONE_CLICK_V1,
                featureCodes.NEPTING_ONE_CLICK_V2
            ]}>
            <Title>
                {intl.formatMessage({
                    id: 'clickandcollect.payment.cards'
                })}
            </Title>
            {cards.map((card: CreditCardInfo, index) => (
                <Card key={index}>
                    <Radio
                        type="checkbox"
                        checked={cardIdSelected === card.id}
                        noMargin
                        onClick={() => {
                            setFieldValue('cardIdSelected', card.id)
                        }}
                        color={COLOR.PRIMARY}>
                        <Row>
                            <CreditCard />
                            <Row>
                                <StyledText>{card.number}</StyledText>
                                <StyledText>
                                    {intl.formatMessage({
                                        id:
                                            'clickandcollect.payment.cards.expiration'
                                    })}
                                </StyledText>
                                <StyledText>{card.expirationDate}</StyledText>
                            </Row>
                        </Row>
                    </Radio>
                </Card>
            ))}
        </FeatureToggle>
    )
}

const NewCard = (props: Props) => {
    const { cards, mealVoucherService, formik, authenticated, intl } = props
    const { values, setFieldValue } = formik
    const { cardIdSelected } = values
    const hasAvailableCards =
        cardIdSelected !== creditCardType.CB &&
        cards &&
        cards.length > 0 &&
        !!cards.find(card => !card.lunchVoucher)
    const [newCardCollapsed, setNewCardCollapsed] = useState(hasAvailableCards)

    useEffect(() => {
        if (!(cards || []).length) {
            setFieldValue('cardIdSelected', creditCardType.CB)
        }
    }, [cards, setFieldValue])

    const maybeCollapsibleLabel = (
        <H4>
            {intl.formatMessage({ id: 'clickandcollect.payment.cards.new' })}
        </H4>
    )
    const maybeCollapsibleContent = (
        <Cards>
            {creditCards.map(({ Icon, labelKey, id }) => {
                const isMealVoucherServiceUnavailable =
                    id === creditCardType.MEAL_VOUCHER && !mealVoucherService
                return (
                    <NewCardContainer
                        key={id}
                        disabled={isMealVoucherServiceUnavailable}>
                        <Radio
                            color={COLOR.PRIMARY}
                            type="checkbox"
                            checked={cardIdSelected === id}
                            noMargin
                            disabled={isMealVoucherServiceUnavailable}
                            onClick={() => {
                                setFieldValue('cardIdSelected', id)
                            }}>
                            <Row>
                                <CardImageContainer>
                                    <Icon size={27} color={COLOR.PRIMARY} />
                                </CardImageContainer>
                                <Text>
                                    <FormattedMessage id={labelKey} />
                                </Text>
                            </Row>
                        </Radio>
                        {authenticated && (
                            <div>
                                {cardIdSelected === id && (
                                    <CheckBox {...props} />
                                )}
                            </div>
                        )}
                        {isMealVoucherServiceUnavailable && (
                            <UnavailableServiceText>
                                <FormattedMessage id="payment.lunch.card.service.unavailable" />
                            </UnavailableServiceText>
                        )}
                    </NewCardContainer>
                )
            })}
        </Cards>
    )
    return (
        <NewCardsContainer margin={!!(cards || []).length}>
            {!(cards || []).length && (
                <>
                    {maybeCollapsibleLabel}
                    {maybeCollapsibleContent}
                </>
            )}
            {!!(cards || []).length && (
                <Collapsible
                    isOpened={!newCardCollapsed}
                    color={COLOR.PRIMARY}
                    onClick={() => {
                        setNewCardCollapsed(!newCardCollapsed)
                        setFieldValue('cardIdSelected', creditCardType.CB)
                    }}
                    label={maybeCollapsibleLabel}>
                    {maybeCollapsibleContent}
                </Collapsible>
            )}
        </NewCardsContainer>
    )
}

const PaymentChoiceComponent = (props: Props) => (
    <>
        <ExistingCards {...props} />
        <NewCard {...props} />
    </>
)

const Title = styled(H4)`
    margin-bottom: 8px;
    ${media.medium`
        margin-bottom: 15px;
    `};
`

const StyledText = styled(Text)`
    white-space: pre;
`

const Card = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`

const Row = styled.div`
    height: 30px;
    flex-direction: row;
    align-items: center;
    display: flex;
    margin-left: 10px;
`

const CardImageContainer = styled.div`
    padding-top: 4px;
    padding-right: 10px;
`

const CheckContainer = styled.div`
    margin: 10px 0 10px 30px;
    ${props => props.lightMargin && 'margin-left: 10px'}
    ${media.medium`
        margin: 10px 0 10px 30px;
    `};
`

const NewCardsContainer = styled.div`
    ${props => props.margin && 'margin-top: 20px;'}
`

const Cards = styled.div`
    margin-top: 8px;
    ${media.medium`
        margin-top: 15px;
    `};
`

const NewCardContainer = styled.div`
    ${props => (props.disabled ? 'opacity: 0.3' : 'cursor: pointer')};
`

const UnavailableServiceText = styled(CaptionText)`
    margin: 0 0 0 30px;
    font-style: italic;
`

export const PaymentChoice = injectIntl(PaymentChoiceComponent)
